/**
 * The "Shared with me" surface's decisions, as pure functions (#1242 / #1247).
 *
 * ### Shared is a PROVENANCE, not a kind of object
 *
 * Jakob, deciding this: *"We need to work like Google Drive. A shared file is
 * just like any other file, except it comes from someone else."*
 *
 * That sentence is the whole design. The two surfaces this replaces partitioned
 * by **which grant table was written** — `fleet.flight_shares` under "Shared",
 * `fleet.object_shares` under "Shared files" — which is a split a user has no
 * way to see. The same CSV was a legitimate subject for either kind of grant, so
 * which door it appeared behind depended on which dialog the granter happened to
 * open. There was no single place answering "what has been shared with me?".
 *
 * So there is one entry point, one row shape (`SharedEntry`), one renderer and
 * one set of actions. What differs between a shared entry and an own file is the
 * OWNER and the PERMISSION, and nothing else.
 *
 * ### What is deliberately NOT unified
 *
 * Two things, and both are correctness rather than taste:
 *
 * 1. **No "both" direction mode.** `routers/file_objects.py` refuses one, and
 *    the flight listing follows suit. Inbound entries are *reached objects* and
 *    outbound entries are *grants*, so a union would page a set whose rows do
 *    not mean the same thing and whose `total` would answer neither question.
 *    The Flights/Files toggle therefore lives INSIDE a direction — every request
 *    this model produces names exactly one.
 *
 * 2. **Reach vs rows is per DIRECTION, not per tab** (revised by #1328). Both
 *    inbound tabs are reach: the Files tab expands a folder share to the objects
 *    it makes reachable, and since STD-029 R-7 the Flights tab expands it to the
 *    flight RECORDS of those objects — so a log dropped into a shared folder next
 *    week shows up on both, with no second share row anywhere. Both outbound tabs
 *    are grants, because a granter already sees its own files and flights and
 *    what is actionable there is the grant.
 *
 *    This used to read "the Flights tab is 1:1 with grants", and the thing that
 *    changed is not taste. Conveyed flights are not the "phantom flight rows"
 *    that formulation was guarding against — those would be flights invented for
 *    shared files that record none. Each of these is a real `fleet.flights` row
 *    the database now admits, and B-6 retires the per-file flight-share menu item
 *    (R-12), so a folder share becomes *how* flights are shared: a Flights tab
 *    reading only the grant table would quietly show nothing while sharing worked.
 */
import type {
  FlightShare,
  ObjectSharePermission,
  ShareCounterparties,
  ShareDirection,
  SharedObject,
} from '../../types/api';
import { isOutboundShare, type DirectionalShare } from '../flight-shares/shareDisplay';
import type { TranslationKey } from '../../i18n/useTranslation';
import type { Translate } from '../../i18n/plural';
import { formatDate } from '../file-browser/utils';
import { entriesForActionBy } from '../file-browser/selectionModel';

/** Which listing the toggle is on. */
export type SharedKind = 'flights' | 'files';

export const SHARED_KINDS: SharedKind[] = ['flights', 'files'];

/**
 * Backend default for both listings, and the page size this view opens on.
 *
 * One constant now that there is one view. The two backends still declare their
 * own `DEFAULT_SHARE_PAGE_SIZE` / `DEFAULT_SHARED_OBJECT_PAGE_SIZE`
 * independently, and that is right — retuning a listing whose unit is "one
 * flight" should not silently retune one whose unit is "one object reached
 * through an ancestor walk". This constant only has to be a value BOTH accept.
 */
export const SHARED_PAGE_SIZE = 50;

/**
 * Capped at the lower of the two backends' maxima (they agree on 200 today).
 * A value above the server's maximum is a 422, not a silent clamp.
 */
export const SHARED_PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

// ---------------------------------------------------------------------------
// Who may see this screen
// ---------------------------------------------------------------------------

/** Who is looking. Two independent facts, never one boolean. */
export interface SharedViewer {
  /** Signed in with a resolved profile, i.e. a member of some organization. */
  isAuthenticated: boolean;
  /** An admin of that organization — the authority `require_org_admin` checks. */
  isOrgAdmin: boolean;
}

/**
 * Whether this viewer may SEE what crosses their organization's boundary.
 *
 * ### This must never become an admin check. (#1206)
 *
 * VISIBILITY and AUTHORITY are different questions, and collapsing them into one
 * `isAdmin` is the bug this surface exists downstream of: a single admin gate
 * guarded both *granting* a share and *viewing* the shared listing, which hid
 * all 48 production flight shares from every non-admin — that is, from almost
 * everyone the feature was built for.
 *
 * The rule, stated so it cannot be re-collapsed by accident:
 *
 * - **Seeing** is plain authenticated read. Neither `GET /api/flight-shares` nor
 *   `GET /api/file-objects/shared` is role-gated; RLS already limits the rows to
 *   what the caller's organization is a party to. A grantee who cannot see what
 *   they were given has been given nothing.
 * - **Granting and revoking** are `require_org_admin`. Those, and only those,
 *   consult `isOrgAdmin` — see `sharedSections`.
 *
 * So this reads `isAuthenticated` and deliberately ignores `isOrgAdmin`. If you
 * are here to add a role check, you are about to reintroduce #1206; the test
 * `canViewShares is NOT an admin check` will fail, and it is meant to.
 */
export function canViewShares(viewer: SharedViewer): boolean {
  return viewer.isAuthenticated;
}

// ---------------------------------------------------------------------------
// One row, whatever it came from
// ---------------------------------------------------------------------------

/**
 * One thing that crosses an organization boundary, as the grid renders it.
 *
 * Both listings map onto this and the renderer sees nothing else — which is what
 * makes "a shared file looks exactly like an own file" true by construction
 * rather than by two components being kept in sync by hand.
 */
export interface SharedEntry {
  /** React key. The share id for a flight, the object id for a file. */
  key: string;
  kind: SharedKind;
  /**
   * The file's name, or `null` when it cannot be known — a revoked flight share
   * (the flight embed is empty, so there is no name to read) or a flight with no
   * registry row yet. The renderer labels those from the drone and date instead;
   * a name is never guessed.
   */
  name: string | null;
  isFolder: boolean;
  /**
   * What `GET /api/file-objects/{object_id}/download` takes, and the only thing
   * that makes an entry openable. `null` for a folder, for a revoked flight
   * share, and for a flight that has no registry row — in every one of those
   * cases a Download button could only 404.
   */
  objectId: string | null;
  /**
   * The registry row a grantee can BROWSE INTO — set only for a FOLDER
   * (STD-029 B-4, closing D-6).
   *
   * A second field rather than widening `objectId`, and the reason is that they
   * answer different questions about the same uuid. `objectId` is the *download*
   * address and is null for a folder on purpose: a folder has no bytes and
   * `/download` answers `FILE_OBJECT_IS_FOLDER`. This is the *navigation*
   * address, and it is null for a FILE for the mirror reason — `/children`
   * answers `FILE_OBJECT_NOT_FOLDER`. Merging them would make each menu item's
   * gate a boolean pair rather than a field, which is how the wrong one gets
   * offered.
   *
   * A revoked entry never reaches this function — neither direction of
   * `/api/file-objects/shared` returns one — so there is no liveness test here.
   * A share revoked between the listing and the click answers 404 from
   * `/children`, which is the same answer Download already gives.
   */
  folderId: string | null;
  /**
   * The grant row, for Revoke. Present on both kinds; whether it may be USED is
   * `sharedSections`' job, because only the granting side may revoke and only an
   * org admin may ask.
   */
  shareId: string;
  /**
   * WHICH TABLE `shareId` names, and therefore which revoke endpoint it belongs
   * to. **Not derivable from `kind`, in either direction** — that is the whole
   * reason the field exists:
   *
   * - a `'files'` entry names a `fleet.object_shares` row *unless* it reached
   *   the listing through a flight share, which since #1249 conveys the one
   *   object its flight owns (`SharedObject.share_kind === 'flight'`);
   * - a `'flights'` entry names a `fleet.flight_shares` row *unless* it reached
   *   the listing through an object share, which since #1328 conveys the flight
   *   records of everything in its reach (`FlightShare.share_kind === 'object'`).
   *
   * So two rows rendered side by side in one section can carry ids from
   * different tables, and the revoke endpoint picked from `kind` alone would
   * resolve one of them against the wrong table — matching nothing and reporting
   * "already revoked" while the access continues.
   */
  shareTable: 'object_shares' | 'flight_shares';
  permission: ObjectSharePermission;
  /** The other organization: the owner inbound, the grantee outbound. */
  counterpartyId: string;
  /** `null` when the server could not name it. Render neutrally, never guess. */
  counterpartyName: string | null;
  grantedAt: string;
  revokedAt: string | null;
  /** Flights only. */
  droneSerial: string | null;
  /** Flights only — display-only, and null once a share is revoked. */
  flightDate: string | null;
  /** Files only. `null` means "not known", which is not the same as `0`. */
  sizeBytes: number | null;
  /** Files only — where it sits inside what was shared, never above it. */
  parentPath: string | null;
  /**
   * Flights only: `fleet.flights.source` (#1613), `'dlb_track'` for a
   * DroneLogbook track that has flight hours and no file. `null` when not known
   * (a revoked share, an older backend), which is labelled as before.
   */
  flightSource: string | null;
}

/**
 * A page of flight shares as entries.
 *
 * `object_id`/`object_name` are what #1247 added to the projection. Before them
 * this listing could say a flight existed and offer no way to open it — which is
 * exactly what the old "Shared" view did, with zero download affordance on a
 * screen whose entire subject was access.
 */
export function flightEntries(shares: FlightShare[]): SharedEntry[] {
  return shares.map((share) => ({
    key: share.id,
    kind: 'flights' as const,
    name: share.object_name ?? null,
    isFolder: false,
    // Usually a `flight_shares` row — but since #1328 an inbound entry can be a
    // flight conveyed by a FOLDER share (STD-029 R-7), whose `share_id` is an
    // `object_shares` id. `share_kind` is optional on the wire for deploy skew,
    // and an older backend only ever sent flight grants, so absent means
    // `flight_shares`, never "unknown" — the exact mirror of `objectEntries`.
    shareTable:
      share.share_kind === 'object' ? ('object_shares' as const) : ('flight_shares' as const),
    // Null on a revoked share, because the backend reads it through the flight
    // embed that revocation empties. Offering a download there would promise
    // access the database withdrew in the same statement.
    objectId: share.object_id ?? null,
    // A flight is never a folder, so there is nothing to browse into.
    folderId: null,
    shareId: share.id,
    permission: share.permission,
    counterpartyId: share.counterparty_organization_id ?? share.grantee_organization_id,
    counterpartyName: share.counterparty_organization_name ?? null,
    grantedAt: share.granted_at,
    revokedAt: share.revoked_at ?? null,
    // The share row's OWN denormalized column, never the embed — which is what
    // keeps a REVOKED share identifiable after the grantee loses SELECT on the
    // flight.
    droneSerial: share.drone_serial ?? null,
    flightDate: share.flight_date ?? null,
    sizeBytes: null,
    parentPath: null,
    flightSource: share.flight_source ?? null,
  }));
}

/**
 * A page of reached objects as entries.
 *
 * Keyed on `object_id` — the registry row id — never on the path or an S3 key. A
 * path is derived by walking `parent_id` and changes the moment an ancestor is
 * renamed; the id does not.
 */
export function objectEntries(objects: SharedObject[]): SharedEntry[] {
  return objects.map((object) => ({
    key: object.object_id,
    kind: 'files' as const,
    name: object.name,
    isFolder: object.is_folder,
    // A folder has no bytes (`blob_key IS NULL`) and asking for them is answered
    // `FILE_OBJECT_IS_FOLDER`. Offering the button and then explaining the
    // refusal would be worse than not offering it.
    objectId: object.is_folder ? null : object.object_id,
    // The mirror of the line above: a folder is the one thing that CAN be
    // opened, and a file is the one thing that cannot. No liveness test is
    // needed here — `SharedObject` carries no `revoked_at` because neither
    // direction of `/api/file-objects/shared` returns a revoked entry at all
    // (inbound is RLS-filtered reach, outbound filters `revoked_at is null`).
    folderId: object.is_folder ? object.object_id : null,
    shareId: object.share_id,
    // A files entry usually names an `object_shares` row, but not always: a
    // flight share conveys the one object its flight owns (#1249), and that
    // entry's `share_id` is a `fleet.flight_shares` id. `share_kind` is optional
    // on the wire for deploy skew, and an older backend only ever sent object
    // shares — so absent means `object_shares`, never "unknown".
    shareTable:
      object.share_kind === 'flight' ? ('flight_shares' as const) : ('object_shares' as const),
    permission: object.permission,
    counterpartyId: object.counterparty_organization_id,
    counterpartyName: object.counterparty_organization_name ?? null,
    grantedAt: object.granted_at,
    // Reach has no revoked state: a revoked share simply stops reaching. The
    // column exists on the entry for the flights tab, which lists revoked grants
    // on purpose (`include_revoked`).
    revokedAt: null,
    droneSerial: null,
    flightDate: null,
    sizeBytes: object.size_bytes ?? null,
    parentPath: parentPathOf(object),
    flightSource: null,
  }));
}

/**
 * The folder part of an entry's path, or `null` when it sits at the share root.
 *
 * The name is the card's title, so repeating it underneath would be noise; what
 * a grantee needs is where it sits inside what they were given. Paths here are
 * already relative to the topmost node the caller may see, so this can never
 * expose a folder name above the share root.
 *
 * Tolerates a path that does not end in the name (a backend that sent a bare
 * name, or a name containing a slash) by returning `null` rather than a
 * mis-sliced string — an approximate path on a screen about access is worse than
 * none.
 */
export function parentPathOf(object: Pick<SharedObject, 'path' | 'name'>): string | null {
  const path = object.path;
  if (!path) return null;
  const suffix = `/${object.name}`;
  if (!path.endsWith(suffix)) return null;
  const parent = path.slice(0, -suffix.length);
  return parent === '' ? null : parent;
}

/**
 * A size to render, or `null` when it is genuinely unknown.
 *
 * `null` bytes and `0` bytes are different facts and are kept apart here, which
 * the shared `formatFileSize` helper cannot do — it returns `''` for both, so an
 * empty file would silently render as though its size were unavailable. The
 * backend was careful to keep the two distinct on the wire; degrading them at
 * the last step would throw that away.
 *
 * A folder has no size of its own — it is not a small file, and reporting one
 * would invite the reader to believe its contents are that big.
 */
export function entrySizeLabel(entry: Pick<SharedEntry, 'isFolder' | 'sizeBytes'>): string | null {
  if (entry.isFolder) return null;
  const bytes = entry.sizeBytes;
  if (bytes == null) return null;
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// Sections: one direction each, never both
// ---------------------------------------------------------------------------

export interface SharedSection {
  /** Stable key, and the i18n suffix for the heading. */
  key: 'outbound' | 'inbound' | 'all';
  /** What to ask the server for. `null` = direction unstated (flights only). */
  direction: ShareDirection | null;
  /** Revoke belongs to the granting side, and to an org admin. */
  allowRevoke: boolean;
}

const UNDIVIDED_SECTIONS: SharedSection[] = [{ key: 'all', direction: null, allowRevoke: false }];

/**
 * The sections to render for a kind.
 *
 * Both directions are always on screen when they can be: a sharing screen
 * showing only what you gave away, or only what you received, answers half of
 * the only question it exists for. Each section is its own request, so the two
 * page independently — a drone-wide grant is one row per flight (~207 for a busy
 * drone).
 *
 * `directionKnown` applies to the FLIGHTS tab only, and it is not a stylistic
 * asymmetry. `SharedObject.direction` is required on the wire and has been since
 * #1205, so a files page always knows. `FlightShare.direction` is optional for
 * deploy skew — frontend and backend are separate Railway services that can
 * deploy minutes apart — and when it cannot be derived the flights listing
 * refuses to split rather than file a grant under the wrong heading, which on
 * this screen is a false statement about who can see the data.
 *
 * Revoke needs BOTH conditions and they are independent. Direction decides which
 * side of a share the caller is on — only the granting side may revoke, and the
 * grantee's attempt is answered 404. `canAdministerShares` is the caller's
 * authority — revoking is `require_org_admin`, so a plain member is answered
 * 403. Seeing a share and being able to withdraw it are different questions, and
 * answering them with one boolean is what previously hid the whole listing from
 * members (#1206).
 */
export function sharedSections(
  kind: SharedKind,
  directionKnown: boolean,
  canAdministerShares: boolean
): SharedSection[] {
  if (kind === 'flights' && !directionKnown) return UNDIVIDED_SECTIONS;
  return [
    { key: 'outbound', direction: 'outbound', allowRevoke: canAdministerShares },
    { key: 'inbound', direction: 'inbound', allowRevoke: false },
  ];
}

/**
 * Whether THIS row gets a Revoke control.
 *
 * The section already folds in direction and authority; what is left is the row
 * itself. A revoked share cannot be revoked again — the API answers 404 on an
 * already-stamped row.
 *
 * The third condition guards the two-tables problem #1249 introduced. `SharedView`
 * picks its revoke endpoint from `entry.kind` alone (`:355` flights, `:357`
 * files), so a `'files'` entry whose `shareId` is a `fleet.flight_shares` id
 * would be posted to `POST /api/object-shares/{id}/revoke`. That resolves
 * `.eq('id', share_id)` against `fleet.object_shares`, matches nothing, and
 * answers *"Share not found, already revoked, or not yours to revoke"* — **while
 * the access continues**. It is the misleading shape of failure, not the loud
 * one: the admin reads "already handled" and the grantee keeps the file.
 *
 * Unreachable today, because `sharedSections` sets `allowRevoke` on the
 * *outbound* section only and a flight-conveyed object is inbound reach. That is
 * exactly why it is asserted here rather than left resting on a section flag:
 * the day an inbound action appears, this refuses instead of mis-firing at
 * another table's row. A flight share is withdrawn on the Flights toggle, via
 * `POST /api/flight-shares/{id}/revoke`, where the grant actually lives.
 *
 * #1328 makes the problem symmetric, so the guard is too. An object share now
 * conveys the flight RECORDS of the files in its reach (STD-029 R-7), so a
 * `'flights'` entry can carry an `object_shares` id — and posting that to
 * `POST /api/flight-shares/{id}/revoke` fails in the same misleading way, with
 * the same consequence. Each kind therefore checks for its OWN table rather
 * than trusting `entry.kind`, which is the fact that stopped being reliable.
 * A conveyed flight is withdrawn by revoking the folder share, on the Files
 * toggle.
 */
export function canRevokeEntry(section: SharedSection, entry: SharedEntry): boolean {
  const ownTable = entry.kind === 'files' ? 'object_shares' : 'flight_shares';
  if (entry.shareTable !== ownTable) return false;
  return section.allowRevoke && !entry.revokedAt;
}

/**
 * Whether THIS row gets a Download control.
 *
 * Authority is not consulted, and that is deliberate: downloading is the whole
 * point of being a grantee, and `GET /api/file-objects/{id}/download` authorizes
 * by asking the database whether the caller may SELECT the row, not by role.
 * What it turns on is whether there is anything to fetch at all — see
 * `SharedEntry.objectId`.
 */
export function canDownloadEntry(entry: SharedEntry): boolean {
  return entry.objectId !== null;
}

/**
 * Whether THIS row can be browsed into (STD-029 B-4, D-6).
 *
 * The action that made object sharing usable rather than merely recorded: a
 * folder share is one row covering a subtree the share table cannot enumerate,
 * so before this the grantee saw an entry named `2026-flights` and had no way to
 * learn what was in it. `GET /api/file-objects/{id}/children` is that way, and
 * the same Files browser renders it — decision 4, "grantees browse into shared
 * folders", and one browser component rather than two that drift.
 */
export function canOpenEntry(entry: SharedEntry): boolean {
  return entry.isFolder && entry.folderId !== null;
}

// ---------------------------------------------------------------------------
// What a card is called, and what its menu offers (#1692)
// ---------------------------------------------------------------------------

/**
 * Is this a shared DroneLogbook track — a flight record with no log file?
 *
 * `'dlb_track'` rows (#1613) are flights whose log never reached the Dashboard:
 * flight hours from DroneLogbook, no file, nothing to download or plot. A drone
 * share conveys them with the rest of the drone's flights, so the grantee sees
 * them here. Gated on `objectId` too: a track never has one, and if it ever did,
 * the file would be the more useful thing to show.
 */
export function isTrackEntry(entry: Pick<SharedEntry, 'flightSource' | 'objectId'>): boolean {
  return entry.flightSource === 'dlb_track' && entry.objectId === null;
}

/**
 * The label on an entry's card — THE ONE PLACE an entry without a file name is
 * named, so relabelling a kind of row is a change here and nowhere else.
 *
 * A name is never guessed. A DroneLogbook track says what it is; another flight
 * with no readable file (revoked, or no registry row yet) is named by the date
 * it carries; and with no date either, it says the file is unavailable.
 */
export function entryLabel(entry: SharedEntry, t: Translate): string {
  if (entry.name) return entry.name;
  if (isTrackEntry(entry)) {
    return entry.flightDate
      ? t('sharedView.trackLabel', { date: formatDate(entry.flightDate) })
      : t('sharedView.trackLabelUndated');
  }
  return entry.flightDate
    ? t('sharedView.flightOn', { date: formatDate(entry.flightDate) })
    : t('sharedView.unnamedEntry');
}

/** The one-line explanation an entry's card carries on hover, or `null` for none. */
export function entryTooltipKey(entry: SharedEntry): TranslationKey | null {
  return isTrackEntry(entry) ? 'sharedView.trackTooltip' : null;
}

/**
 * A section's entries in the order the grid renders them: grouped by
 * counterparty. Shift-click ranges and Ctrl+A walk this order, so it must be
 * the same function the panel renders from, not a second sort that could drift.
 */
export function renderedEntries(entries: SharedEntry[]): SharedEntry[] {
  return groupByCounterparty(entries).flatMap((group) => group.entries);
}

/** What the context menu offers for a right-click, given the selection. */
export interface SharedMenuModel {
  /** The entries a Download fetches: the selection's downloadable ones. */
  downloadTargets: SharedEntry[];
  /** The single-entry items, each `false` when more than one entry is targeted. */
  canOpen: boolean;
  canPlot: boolean;
  canRevoke: boolean;
  /** No item at all: the menu stays shut and the card shows no ⋮ button. */
  isEmpty: boolean;
}

/**
 * The menu for a right-click on `clicked`, the Files browser's rule: act on the
 * whole selection when the clicked entry is part of it, otherwise on the
 * clicked entry alone (`entriesForActionBy`).
 *
 * Download is the one item that takes many entries. Open, Plot and Unshare act
 * on one entry and are left out when several are targeted, rather than acting
 * on the one under the cursor: an Unshare that withdrew one grant out of five
 * selected would read as withdrawing all five.
 *
 * Every per-entry rule is the existing one (`canDownloadEntry`, `canOpenEntry`,
 * `canPlotEntry`, `canRevokeEntry`), so a DroneLogbook track gets no menu on the
 * grantee's side (nothing to download, open or plot, and a grantee cannot
 * revoke) and Unshare alone on the grantor's.
 */
export function sharedMenuModel(
  clicked: SharedEntry,
  section: SharedSection,
  selectedKeys: ReadonlySet<string>,
  visible: readonly SharedEntry[]
): SharedMenuModel {
  const targets = entriesForActionBy(clicked, selectedKeys, visible, (entry) => entry.key);
  const single = targets.length === 1;
  const downloadTargets = targets.filter(canDownloadEntry);
  const canOpen = single && canOpenEntry(clicked);
  const canPlot = single && canPlotEntry(clicked);
  const canRevoke = single && canRevokeEntry(section, clicked);
  return {
    downloadTargets,
    canOpen,
    canPlot,
    canRevoke,
    isEmpty: downloadTargets.length === 0 && !canOpen && !canPlot && !canRevoke,
  };
}

/** Whether `entry`'s card offers a menu on its own (the ⋮ button). */
export function entryHasMenu(entry: SharedEntry, section: SharedSection): boolean {
  return !sharedMenuModel(entry, section, new Set(), [entry]).isEmpty;
}

/** Extensions the Analytics viewer can actually plot. */
const PLOTTABLE = /\.(csv|bin)$/i;

/**
 * Whether THIS row may be opened in Analytics.
 *
 * Needs a name to decide from — the viewer picks its parser off the extension —
 * and an id to address, for the reason in `analyticsHandoffEntry`. A revoked
 * flight share fails both, which is correct: there is nothing to open.
 */
export function canPlotEntry(entry: SharedEntry): boolean {
  if (!canDownloadEntry(entry) || !entry.name) return false;
  return PLOTTABLE.test(entry.name);
}

// ---------------------------------------------------------------------------
// Grouping and filtering
// ---------------------------------------------------------------------------

/** One counterparty's entries, as the view renders them. */
export interface CounterpartyGroup {
  organizationId: string;
  /** `null` when the server could not name it. Render neutrally, never guess. */
  organizationName: string | null;
  entries: SharedEntry[];
}

/**
 * Group entries by the organization on the other side.
 *
 * One grouping rule for both kinds, and it is the question a person actually has
 * on this screen: "who gave us this", or on the outbound side "who did we give
 * it to". The flights view previously grouped by drone instead; the serial has
 * not been lost, it moved onto the card, where it is a property of the row like
 * a file's size. Grouping by share row was never an option for files — one
 * folder grant can reach forty files, so it would render forty rows under a
 * heading naming a share the viewer never saw.
 *
 * Groups are ordered by name so the list does not reshuffle between renders; an
 * org the payload could not name sorts last under its id rather than under an
 * empty string, where it would displace real names. Entries keep the server's
 * order within a group — it is already sorted, and re-sorting client-side would
 * only reorder the current page, which reads as a bug while paging.
 */
export function groupByCounterparty(entries: SharedEntry[]): CounterpartyGroup[] {
  const groups = new Map<string, CounterpartyGroup>();

  for (const entry of entries) {
    const existing = groups.get(entry.counterpartyId);
    if (existing) {
      existing.entries.push(entry);
      // A name learned on a later row upgrades the group rather than being
      // dropped — the first row is not authoritative about the org's name.
      if (existing.organizationName === null && entry.counterpartyName) {
        existing.organizationName = entry.counterpartyName;
      }
      continue;
    }
    groups.set(entry.counterpartyId, {
      organizationId: entry.counterpartyId,
      organizationName: entry.counterpartyName,
      entries: [entry],
    });
  }

  return [...groups.values()].sort(byNameThenId);
}

function byNameThenId(
  a: { organizationName: string | null; organizationId: string },
  b: { organizationName: string | null; organizationId: string }
): number {
  if (a.organizationName && b.organizationName) {
    return a.organizationName.localeCompare(b.organizationName);
  }
  if (a.organizationName) return -1;
  if (b.organizationName) return 1;
  return a.organizationId.localeCompare(b.organizationId);
}

/** An entry in a counterparty dropdown. `name` is null when unresolved. */
export interface CounterpartyOption {
  id: string;
  name: string | null;
}

/**
 * Fold a page of entries into the counterparty dropdown's options.
 *
 * Additive, and that is the point. A *filtered* page only contains the
 * counterparty it was filtered to, so recomputing options from the current page
 * alone would collapse the dropdown to the chosen org and strand the user there
 * with no control left to undo the filter. Accumulating means paging and
 * filtering can only ever reveal more counterparties, never fewer.
 *
 * The options are built from the entries actually returned rather than from
 * `/{flight,object}-shares/organizations`, which is `require_org_admin` and
 * would leave a plain member with an empty dropdown.
 *
 * Returns `previous` unchanged when it learns nothing, so a caller can hold this
 * in state and merge on every render without looping.
 */
export function mergeCounterpartyOptions(
  previous: CounterpartyOption[],
  entries: Pick<SharedEntry, 'counterpartyId' | 'counterpartyName'>[]
): CounterpartyOption[] {
  const byId = new Map(previous.map((o) => [o.id, o]));
  let changed = false;

  for (const entry of entries) {
    const id = entry.counterpartyId;
    const name = entry.counterpartyName;
    const existing = byId.get(id);
    if (existing && (existing.name !== null || name === null)) continue;
    // Either new, or previously nameless and now named — a name learned later
    // should upgrade the entry rather than be dropped.
    byId.set(id, { id, name });
    changed = true;
  }

  if (!changed) return previous;

  // Named options first, alphabetically; the unnamed sink to the bottom rather
  // than sorting under an empty string and displacing real names.
  return [...byId.values()].sort((a, b) => {
    if (a.name && b.name) return a.name.localeCompare(b.name);
    if (a.name) return -1;
    if (b.name) return 1;
    return a.id.localeCompare(b.id);
  });
}

/**
 * The label for a dropdown entry, or `undefined` when the org cannot be named.
 *
 * The counterparties call is consulted only as a fallback, and only helps an
 * admin — the caller turns `undefined` into a neutral label, never a guess.
 */
export function counterpartyOptionName(
  option: CounterpartyOption,
  counterparties?: ShareCounterparties
): string | undefined {
  return option.name ?? counterparties?.organizations.find((o) => o.id === option.id)?.name;
}

// ---------------------------------------------------------------------------
// Paging
// ---------------------------------------------------------------------------

/** The `offset` for a zero-based page. */
export function pageOffset(page: number, pageSize: number): number {
  return Math.max(0, page) * pageSize;
}

/**
 * Everything one section knows about its own listing.
 *
 * One object rather than three `useState`s, and `total` lives here beside the
 * page it constrains: they are only meaningful together — clamping a page needs
 * the total, and the total is what the pager counts. Split apart, they drift.
 */
export interface SectionListState {
  /** '' = no counterparty filter. */
  counterpartyId: string;
  /**
   * Show revoked grants too (flights only — reach has no revoked state).
   *
   * Per SECTION since #1498, not per screen: it is a filter on one listing,
   * so it sits beside that listing's counterparty filter and re-scopes only
   * that result set. "Which grants did we withdraw?" and "which grants were
   * withdrawn from us?" are separate questions with separate answers.
   */
  includeRevoked: boolean;
  page: number;
  /**
   * Rows matching the filters, as last reported by the server — remembered, not
   * read off the current render. See `withLoadedPage`.
   */
  total: number;
}

export const INITIAL_SECTION_STATE: SectionListState = {
  counterpartyId: '',
  includeRevoked: false,
  page: 0,
  total: 0,
};

/**
 * Choose a counterparty — which necessarily returns to the first page.
 *
 * A different counterparty is a different result set, so the old page number
 * describes nothing. Carried over, it would request an offset into a set that
 * may be shorter, and the empty page that comes back is one `canClaimEmpty` is
 * then entitled to describe as "nothing is shared" — the exact false statement
 * this module exists to prevent.
 */
export function withCounterparty(
  state: SectionListState,
  counterpartyId: string
): SectionListState {
  if (state.counterpartyId === counterpartyId) return state;
  return { ...state, counterpartyId, page: 0 };
}

/**
 * Toggle revoked grants in or out of this section's listing.
 *
 * Re-scopes the result set, so the page goes back to the first — page 3 of
 * the live-only set indexes nothing in the set with revocations folded in.
 * Same rule, and the same same-object short-circuit, as `withCounterparty`.
 */
export function withIncludeRevoked(
  state: SectionListState,
  includeRevoked: boolean
): SectionListState {
  if (state.includeRevoked === includeRevoked) return state;
  return { ...state, includeRevoked, page: 0 };
}

/** Move to a page of the same result set. */
export function withPage(state: SectionListState, page: number): SectionListState {
  const next = Math.max(0, page);
  if (state.page === next) return state;
  return { ...state, page: next };
}

/**
 * Return to the first page, keeping the counterparty.
 *
 * For the controls that resize or re-scope the result set without changing which
 * counterparty it is about: page size, the include-revoked switch, and the
 * Flights/Files toggle — page 3 of the flights set indexes nothing in the files
 * one.
 */
export function resetToFirstPage(state: SectionListState): SectionListState {
  return withPage(state, 0);
}

/**
 * Fold a freshly-arrived page into the remembered total and current page.
 *
 * One function for both listings, taking the row COUNT rather than an envelope,
 * because the two envelopes differ only in whether the array is called `shares`
 * or `objects` — and two copies of these rules would drift, with the copy that
 * drifted telling someone their data is private when nobody checked.
 *
 * **Absent data must change nothing.** Between pages the query key changes and
 * `data` is briefly `undefined`; treating that as "total 0" would take the pager
 * off screen mid-click and clamp the page the user just asked for back to the
 * first, so paging forward could never stick. A FAILED fetch is the same shape,
 * and must not be mistaken for the end of the list either.
 *
 * When a page HAS arrived, its total is remembered and the current page is
 * clamped into range — result sets shrink under the viewer when "include
 * revoked" is switched off or somebody else revokes a grant.
 *
 * Returns `state` unchanged when nothing moved, so callers can apply it on every
 * render without looping.
 */
export function withLoadedPage(
  state: SectionListState,
  page: { rowCount: number; total?: number } | undefined,
  pageSize: number
): SectionListState {
  if (!page) return state;
  // `total` is absent only from an unpaginated backend, which returned the whole
  // set as one page — so the page length IS the total there.
  const total = page.total ?? page.rowCount;
  const clamped = clampPage(state.page, total, pageSize);
  if (total === state.total && clamped === state.page) return state;
  return { ...state, total, page: clamped };
}

/**
 * The page that is still in range for `total` rows.
 *
 * The result set can shrink under the viewer — turning off "include revoked",
 * filtering to one counterparty, or somebody else revoking a grant — and an
 * offset past the end returns an empty page, which the view would otherwise be
 * entitled to describe as "nothing here yet".
 */
export function clampPage(page: number, total: number, pageSize: number): number {
  if (pageSize <= 0) return 0;
  const lastPage = Math.max(0, Math.ceil(total / pageSize) - 1);
  return Math.min(Math.max(0, page), lastPage);
}

/**
 * Whether an empty list may be described as "nothing is shared".
 *
 * Only when the fetch actually succeeded. A failed request also yields no rows,
 * and reporting that as "nothing is shared" states an unknown as a fact — on
 * this screen specifically, it tells someone nobody has given them anything when
 * nobody has checked.
 *
 * `rowCount` is deliberately the count the SERVER returned, not the count left
 * after the view's own direction filter: rows dropped there are rows we hold but
 * cannot place, which is not the same as none existing.
 */
export function canClaimEmpty(fetchSucceeded: boolean, rowCount: number): boolean {
  return fetchSucceeded && rowCount === 0;
}

/**
 * Which sentence an EMPTY section shows, once `canClaimEmpty` allows one.
 *
 * "Nothing here yet." was the only content of a whole panel and said nothing
 * about what would put something there (#1498). The answer differs by
 * direction and by kind: an empty outbound panel points at the action that
 * fills it, an empty inbound panel says who would have to act. A filtered
 * empty set is a different claim again — "nothing with THIS organization" —
 * and keeps its own line whatever the section. The undivided fallback
 * section mixes both directions, so it can only make the neutral claim.
 */
export function emptyStateKey(
  section: SharedSection['key'],
  kind: SharedKind,
  filtered: boolean
): TranslationKey {
  if (filtered) return 'sharedView.noneForOrg';
  if (section === 'outbound') {
    return kind === 'flights' ? 'sharedView.emptyOutboundFlights' : 'sharedView.emptyOutboundFiles';
  }
  if (section === 'inbound') {
    return kind === 'flights' ? 'sharedView.emptyInboundFlights' : 'sharedView.emptyInboundFiles';
  }
  return 'sharedView.noneShared';
}

// ---------------------------------------------------------------------------
// Direction, for the flights listing
// ---------------------------------------------------------------------------

/** A share as this model reads it — direction is server-sent but optional. */
export interface DirectedShare extends DirectionalShare {
  direction?: ShareDirection;
}

/**
 * Which side of a share the caller is on, or `null` when that cannot be known.
 *
 * The server says so directly. Failing that (an older backend), the caller's own
 * org id decides it. Failing both, the answer is `null` — never a guess.
 * Direction is not cosmetic: only the granting org may revoke, so a wrong
 * direction offers a control that is answered 404, on the screen whose entire
 * subject is who can see what.
 */
export function resolveShareDirection(
  share: DirectedShare,
  ownOrganizationId?: string | null
): ShareDirection | null {
  if (share.direction === 'inbound' || share.direction === 'outbound') return share.direction;
  if (!ownOrganizationId) return null;
  return isOutboundShare(share, ownOrganizationId) ? 'outbound' : 'inbound';
}

/**
 * Whether THESE shares may be split into "shared by us" and "shared with us".
 *
 * True as soon as the caller's own org id is known, since that alone settles
 * every row. Otherwise every share here must carry its own direction. A question
 * about a set of rows, so an empty set is vacuously true — see
 * `nextDirectionKnown` for the stateful question, which is not the same one.
 */
export function canSplitByDirection(
  shares: DirectedShare[],
  ownOrganizationId?: string | null
): boolean {
  if (ownOrganizationId) return true;
  return shares.every((s) => s.direction === 'inbound' || s.direction === 'outbound');
}

/**
 * Whether the VIEW may split, given what the latest page taught it.
 *
 * Distinct from `canSplitByDirection` because the view's flag is a belief about
 * the *backend* — does its payload carry `direction`? — while that predicate asks
 * about a set of rows. An empty page is not evidence either way, and must leave
 * the belief alone: re-deriving from rows would let a page that returned nothing
 * (a counterparty filter matching nothing, an offset past the end, "include
 * revoked" hiding the last row) vacuously re-assert a split the view had already
 * abandoned on evidence, momentarily filing every share under "Shared by us"
 * with a Revoke button the grantee side cannot use.
 *
 * The own org id still settles it outright, and a non-empty page can still
 * revoke the belief — or restore it, once a redeployed backend starts sending
 * `direction` mid-session.
 */
export function nextDirectionKnown(
  current: boolean,
  shares: DirectedShare[],
  ownOrganizationId?: string | null
): boolean {
  if (ownOrganizationId) return true;
  if (shares.length === 0) return current;
  return canSplitByDirection(shares, ownOrganizationId);
}

/**
 * The rows of a flights page that belong in a section.
 *
 * A no-op against the current backend, which already filtered by `direction`. It
 * matters against one that ignored the parameter: rather than trust the server's
 * silence, each row is placed by its own resolved direction, and a row whose
 * direction is unknowable is left out — the view then falls back to the undivided
 * list, so it is dropped from a section, never from the screen.
 */
export function sectionShares(
  shares: FlightShare[],
  direction: ShareDirection | null,
  ownOrganizationId?: string | null
): FlightShare[] {
  if (direction === null) return shares;
  return shares.filter((s) => resolveShareDirection(s, ownOrganizationId) === direction);
}

// Per-row mutation state used to live here. It moved to
// `src/utils/pendingTargetId.ts` with #1312 — six call sites across four
// directories made "the shared view's view-model" the wrong owner for a rule
// every per-row button follows. Not re-exported: import it from `utils/`.
